import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { ArrowUpSquareFill } from 'react-bootstrap-icons';
import { getManagementCatchmentByName, getRiverSubUnitByName } from '../store/reducers/fmAPI.js';

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';

import RiverOperationalCatchments from '../components/RiverOperationalCatchments.js';
import Loading from '../components/Loading.js';
import GoverningBodyName from '../components/GoverningBodyName';
import CentralLink from '../components/CentralLink.js';

import MapViewHeaderComponent from '../components/MapViewHeaderComponent.js';

import BreadcrumbComponent from '../components/BreadcrumbComponent.js';

import RainfallMultiStationSummary from '../components/RainfallMultiStationSummary.js';
import StationChainViewer from '../components/StationChainViewer.js';

import CommonHeaders from '../components/helmet-headers/CommonHeaders.js';
import Breadcrumbs, { CRUMBS } from '../components/helmet-headers/Breadcrumbs.js';
import GoverningBodyFlag from '../components/GoverningBodyFlag.js';

import loadable from '@loadable/component';

import TLDHelper from '../lib/tldHelper.js';

const StationMapMarkerLegendComponent = loadable(() => import('../components/StationMapMarkerLegendComponent.js'), { ssr: false });
const StationMapMarkerComponent = loadable(() => import('../components/StationMapMarkerComponent.js'), { ssr: false });
const NearByStationsComponent = loadable(() => import('../components/NearByStationsComponent.js'));

function ManagementCatchmentsView({ mode }) {
	const params = useParams();
	const dispatch = useDispatch();
	const { managementCatchment, riverSubUnit } = useSelector(state => state.fmAPI);

	useEffect(() => {
		if(mode === 'river-sub-units') {
			dispatch(getRiverSubUnitByName(params.riverSubUnitName));
		} else {
			dispatch(getManagementCatchmentByName(params.managementCatchmentName));
		}
	}, [dispatch, params.managementCatchmentName, mode]);

	if(mode !== 'river-sub-units' && mode !== 'management-catchments') {
		return <span>Unknown error</span>;	
	}

	if(!managementCatchment[params.managementCatchmentName] && !riverSubUnit[params.riverSubUnitName]) {
		return <Loading />;
	}

	let activeCatchment = null;
	let descriptionType = 'Management Catchment';
	if(mode === 'river-sub-units') {
		activeCatchment = riverSubUnit[params.riverSubUnitName];
		descriptionType = 'River Sub Unit';
	} else {
		activeCatchment = managementCatchment[params.managementCatchmentName];
	}


	const catchmentName = activeCatchment.mgmtCatchmentName || activeCatchment.subUnitName;
	const governingBody = activeCatchment.governingBody;
	const riverBasinName = activeCatchment.riverBasinName;

	return <div>
		<CommonHeaders
			title={descriptionType+" - "+catchmentName}
			canonicalUrlPath={"/data-explorer/" + (mode === 'river-sub-units' ? "river-sub-units" : "management-catchments") + "/"+catchmentName}
			canonicalDefaultWebsite={TLDHelper.mapGoverningBodyToPrimaryWebsite(governingBody)}
			description={"View catchment summary information, such as river levels and rainfall, for the "+ catchmentName +" "+descriptionType+"."}
		/>
		<Breadcrumbs
			builtinCrumbs={[CRUMBS.HOME, CRUMBS.DATA_EXPLORER/*, CRUMBS.MANAGEMENT_CATCHMENTS*/]}
			leafCrumbs={[{
				name: catchmentName,
				path: "/data-explorer/management-catchments/"+catchmentName
			}]}
		/>
		<Row className="screen-bottom-border">
			<Col xs={12} lg={1} xl={2} className="d-none d-lg-block large-screen-left">&nbsp;</Col>
			<Col xs={12} lg={10} xl={8}>
				<BreadcrumbComponent
						riverBasinName={riverBasinName}
					mgmtCatchmentName={null}
					operCatchmentName={null}
					waterBodyName={null}
					leafText={catchmentName}
				/>
				<Row>
					<Col xs={12}>
						{activeCatchment && (activeCatchment.polygon || activeCatchment.geoJSON) ? <MapViewHeaderComponent
								label=<span><GoverningBodyFlag governingBody={governingBody} padRight/>{catchmentName}</span>
								geoJSON={activeCatchment.polygon ? activeCatchment.polygon : activeCatchment.geoJSON}
								isStatic={false}
								overrideHeight="330px"
							>
								<StationMapMarkerLegendComponent />
								{activeCatchment.stations && activeCatchment.stations.map(function(station) {
									return <StationMapMarkerComponent
										stationName={station.stationName}
										riverName={station.riverName}
										stationPrimaryMetricType={station.stationPrimaryMetricType}
										stationId={station.stationId}
										lat={station.lat}
										lng={station.lng}
										typicalRangeHigh={station.typicalRangeHigh}
										currentHeight={station.currentHeight}
									/>
								})}
							</MapViewHeaderComponent> : <Row className="bg-dark text-white">
								<Col xs={12}><center><h1 className="h1-blendin hX-scale15em">{catchmentName}</h1></center></Col>
							</Row>
						}
					</Col>
				</Row>
				{riverBasinName && <Row style={{borderBottom: "1px dashed grey"}}>
					<Col xs={1}>&nbsp;</Col>
					<Col xs={7}>This {descriptionType} is part of the <b><i>{riverBasinName}</i></b> river basin and is managed by <b><i><GoverningBodyName governingBody={governingBody} /></i></b></Col>
					<Col xs={3} style={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: "2px", marginBottom: "2px" }}>
					    <CentralLink to={"/data-explorer/basins/"+riverBasinName}>
					    <Button variant="dark" size="sm">
					            View River Basin <ArrowUpSquareFill />
					        </Button>
					    </CentralLink>
				    </Col>
					<Col xs={1}>&nbsp;</Col>
				</Row>}
				<Row>
					<Col xs={12} className="bg-dark text-white header-bold" style={{marginBottom: "7px"}}>{descriptionType} Data</Col>
					{activeCatchment && activeCatchment.stationHierarchyData ? <Col xs={12}>
						<Row>
							<Col xs={12}>
								<div style={{padding: "3px 3px 3px 3px"}}>
									<StationChainViewer rootStations={activeCatchment.stationHierarchyData.rootStations || []} stationTrees={activeCatchment.stationHierarchyData.stationTrees || []} stationData={activeCatchment.stations || {}} />
								</div>
							</Col>
						</Row>
					</Col> : null}
					<Col xs={12} md={6}>
						<div style={{padding: "3px 3px 3px 3px"}}>
							<RainfallMultiStationSummary stations={activeCatchment.rainfall} summaryType={mode === 'river-sub-units' ? 'sub unit' : 'catchment'}/>
						</div>
					</Col>
					<Col xs={12} md={6}>
						<Row>
							<Col xs={12} xl={1} className="d-none d-xl-block">&nbsp;</Col>
							<Col xs={12} xl={10}>
								{mode === 'management-catchments' ? <RiverOperationalCatchments catchments={activeCatchment.operationalCatchments} /> : null}
								{activeCatchment.stations && activeCatchment.stations.length > 0 ? <Row>
									<Col xs={12}>
										<NearByStationsComponent headingText={mode === 'management-catchments' ? "Catchment Stations" : "Sub Unit Stations"} stations={activeCatchment.stations} overrideIncludeTypes={['Rainfall', 'Stage', 'Downstream Stage', 'Tidal']} />
									</Col>
								</Row> : null}
							</Col>
							<Col xs={12} xl={1} className="d-none d-xl-block">&nbsp;</Col>
						</Row>
					</Col>
				</Row>
				
			</Col>
			<Col xs={12} lg={1} xl={2} className="d-none d-lg-block large-screen-right">&nbsp;</Col>
		</Row>
	</div>;
}

export default ManagementCatchmentsView;